/**
 * Google Maps Loader Helper
 * Unified loader using @googlemaps/js-api-loader v1.16.8 (Official Google library)
 * Compatible with @react-google-maps/api
 * 
 * IMPORTANT: v1.16+ requires importLibrary() to load constructors like Map, Marker, etc.
 */

import { Loader } from '@googlemaps/js-api-loader';
import { googleMapAPI } from '../config';

// Singleton instance to prevent multiple loads
let loaderInstance = null;
let loadPromise = null;

/**
 * Get or create the Google Maps Loader instance
 * @returns {Loader} The loader instance
 */
export const getLoader = () => {
  if (!loaderInstance) {
    loaderInstance = new Loader({
      apiKey: googleMapAPI,
      version: 'weekly',
      libraries: ['places', 'geometry', 'marker'],
    });
  }
  return loaderInstance;
};

/**
 * Load Google Maps API (singleton - only loads once)
 * Uses importLibrary() for v1.16+ compatibility
 * @returns {Promise<typeof google>} The google maps object
 */
export const loadGoogleMaps = async () => {
  // Return cached promise if already loading/loaded
  if (loadPromise) {
    return loadPromise;
  }

  // Check if already loaded with Map constructor
  if (typeof window !== 'undefined' && window.google && window.google.maps && window.google.maps.Map) {
    return window.google;
  }

  const loader = getLoader();
  
  // v1.16+ requires importLibrary to get constructors
  loadPromise = (async () => {
    // Load core maps library first - this provides Map constructor
    const { Map } = await loader.importLibrary('maps');
    
    // Load additional libraries
    await Promise.all([
      loader.importLibrary('places'),
      loader.importLibrary('geometry'),
    ]);
    
    // Ensure Map constructor is available on window.google.maps
    if (window.google && window.google.maps && !window.google.maps.Map) {
      window.google.maps.Map = Map;
    }
    
    return window.google;
  })();

  return loadPromise;
};

/**
 * Check if Google Maps is loaded with Map constructor
 * @returns {boolean}
 */
export const isGoogleMapsLoaded = () => {
  return typeof window !== 'undefined' && 
         window.google && 
         window.google.maps && 
         typeof window.google.maps.Map === 'function';
};

/**
 * Load specific Google Maps library
 * @param {string} library - Library name (places, geometry, drawing, maps, etc.)
 * @returns {Promise<any>} The library
 */
export const loadGoogleMapsLibrary = async (library) => {
  const loader = getLoader();
  return loader.importLibrary(library);
};

export default {
  loadGoogleMaps,
  isGoogleMapsLoaded,
  loadGoogleMapsLibrary,
  getLoader,
};
